import { useContext } from 'react';
import { differenceInYears } from 'date-fns';
import { NotificationContext } from '../../shared/components/Notification/NotificationContext';
import {
  BookingDataResponse,
  Companion,
  CompanionEnum,
} from './BookingManagment/bookingManagment.interface';

interface UseEnsureCompanionsAge {
  isCompanionsAgeValid: (bookingResponseData: BookingDataResponse) => boolean;
  redirectToHomePage: () => void;
}

/**
 * Hook to validate that companions' ages match their dates of birth
 * @returns Function to validate companions' ages
 */
const useEnsureCompanionsAge = (): UseEnsureCompanionsAge => {
  const { showNotification } = useContext(NotificationContext);

  /**
   * Validates that the age of each companion matches their date of birth
   * @param bookingResponseData The booking response data containing companions
   * @returns true if all ages match their dates of birth, false otherwise
   */
  const isCompanionsAgeValid = (
    bookingResponseData: BookingDataResponse,
  ): boolean => {
    if (!bookingResponseData?.companions) {
      return true; // No companions to validate
    }

    const { companions } = bookingResponseData;

    const isValid = companions.every((companion: Companion, index: number) => {
      const dateOfBirth = companion[CompanionEnum.DateOfBirth];
      const declaredAge = companion[CompanionEnum.Age];

      if (!dateOfBirth || typeof declaredAge !== 'number') {
        showNotification('missingCompanionAgeOrDateOfBirth', 'error', false);
        return false;
      }

      const calculatedAge = differenceInYears(
        new Date(),
        new Date(dateOfBirth),
      );
      const isAgeValid = calculatedAge === declaredAge;

      return isAgeValid;
    });

    return isValid;
  };
  const redirectToHomePage = (): void => {
    try {
      const currentUrl = new URL(window.location.href);

      // Remove the specific query parameters
      currentUrl.searchParams.delete('quoteId');
      currentUrl.searchParams.delete('bookingDataId');
      currentUrl.searchParams.delete('token');

      // Redirect to the cleaned URL
      window.location.href = currentUrl.toString();
    } catch (error) {
      console.error('Failed to redirect:', error);
      // Fallback - just reload the page
      window.location.reload();
    }
  };

  return { isCompanionsAgeValid, redirectToHomePage };
};

export default useEnsureCompanionsAge;
